import { useMemo, useState } from "react";
import { Button, Flex, Input, Select, Space, Typography, message } from "antd";
import { Sender } from "@ant-design/x";
import {
  ChatChannel,
  ChatConversationDetail,
} from "@rumble-it/common-lib/types/models/ChatConversation.interface";
import {
  useChatTemplates,
  useSendChatMessageOnConversation,
} from "../../../../lib/hooks/useChat";
import { chatChannelConfig } from "../data/chatChannelConfig";

const { Text } = Typography;

const WINDOW_MS = 24 * 60 * 60 * 1000;

export const ChatComposer = ({
  conversation,
}: {
  conversation: ChatConversationDetail;
}) => {
  const channel: ChatChannel = conversation.channel;
  const channelConfig = chatChannelConfig[channel];

  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [templateVariables, setTemplateVariables] = useState<
    Record<string, string>
  >({});
  const [showTemplate, setShowTemplate] = useState(false);

  const withinWindow = useMemo(() => {
    if (!conversation.lastInboundAt) {
      return false;
    }

    return (
      Date.now() - new Date(conversation.lastInboundAt).getTime() < WINDOW_MS
    );
  }, [conversation.lastInboundAt]);

  const templateRequired = channelConfig.supportsTemplates && !withinWindow;

  const templatesQuery = useChatTemplates(channelConfig.supportsTemplates);
  const sendMutation = useSendChatMessageOnConversation();

  const selectedTemplate = useMemo(
    () => templatesQuery.data?.find((template) => template.id === templateId),
    [templatesQuery.data, templateId],
  );

  const send = (
    input:
      | { body: string }
      | { templateId: string; templateVariables: Record<string, string> },
  ) => {
    sendMutation.mutate(
      { conversationId: conversation.id, input },
      {
        onSuccess: () => {
          setBody("");
          setTemplateId(undefined);
          setTemplateVariables({});
          setShowTemplate(false);
        },
        onError: () => {
          message.error(
            "Versturen mislukt. Buiten het 24-uursvenster kan alleen een template worden verstuurd.",
          );
        },
      },
    );
  };

  const templateBlock = (
    <Space orientation="vertical" size={8} style={{ width: "100%" }}>
      <Select
        size="small"
        style={{ width: "100%" }}
        placeholder="Kies een template"
        value={templateId}
        onChange={(value) => {
          setTemplateId(value);
          setTemplateVariables({});
        }}
        options={templatesQuery.data?.map((template) => ({
          value: template.id,
          label: `${template.name} (${template.language})`,
        }))}
        loading={templatesQuery.isLoading}
      />

      {selectedTemplate?.variables.map((variable) => (
        <Input
          key={variable}
          size="small"
          placeholder={variable}
          value={templateVariables[variable] ?? ""}
          onChange={(event) =>
            setTemplateVariables((current) => ({
              ...current,
              [variable]: event.target.value,
            }))
          }
        />
      ))}

      {selectedTemplate ? (
        <Button
          type="primary"
          size="small"
          loading={sendMutation.isPending}
          onClick={() =>
            send({
              templateId: selectedTemplate.id,
              templateVariables,
            })
          }
        >
          Template versturen
        </Button>
      ) : null}
    </Space>
  );

  return (
    <Flex vertical gap={8}>
      {!templateRequired ? (
        <Sender
          value={body}
          onChange={setBody}
          loading={sendMutation.isPending}
          placeholder={`Bericht via ${channelConfig.label}...`}
          onSubmit={() => body.trim() && send({ body: body.trim() })}
          submitType="enter"
        />
      ) : null}

      {templateRequired ? (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Laatste inkomende bericht is ouder dan 24 uur. Antwoorden kan alleen
            met een template.
          </Text>
          {templateBlock}
        </>
      ) : channelConfig.supportsTemplates ? (
        showTemplate ? (
          templateBlock
        ) : (
          <Button
            type="link"
            size="small"
            style={{ alignSelf: "flex-start", padding: 0 }}
            onClick={() => setShowTemplate(true)}
          >
            Template gebruiken
          </Button>
        )
      ) : null}
    </Flex>
  );
};
